/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */

import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import moment from 'moment-timezone';

import { pdfSafeValue, currency, deSlug } from '@global/helpers';
import Footer from './Footer.web';
import Header from './Header.web';
import { PAGE_SIZE } from './constant';

const NAVY = '#142151';
const BORDER = '#ccc';
const STRIPE = '#EEF3FC';

const COLUMNS = [
  { key: 'no', label: 'No', width: '3%' },
  { key: 'invoice_no', label: 'Invoice No.', width: '13%' },
  { key: 'invoice_date', label: 'Invoice Date', width: '6%' },
  { key: 'due_date', label: 'Due Date', width: '8%' },
  { key: 'customer_code', label: 'Customer Code', width: '7%' },
  { key: 'customer_name', label: 'Customer Name', width: '19%' },
  {
    key: 'invoice_age',
    label: 'Invoice Age (days)',
    width: '7%',
    textAlign: 'right',
  },
  {
    key: 'outstanding_amount',
    label: 'Outstanding Amount',
    width: '8%',
    textAlign: 'right',
  },
  { key: 'payment_method', label: 'Payment Method', width: '6%' },
  {
    key: 'payment_amount',
    label: 'Payment Amount',
    width: '6%',
    textAlign: 'right',
  },
  { key: 'notes', label: 'Notes', width: '17%' },
];

const PdfInvoiceCollection = ({ data }) => {
  const salesman = data?.salesman || {};
  const preparedBy = data?.prepared_by || {};
  const invoices = data?.invoices || [];
  const isVn = (salesman.is_active_for || []).includes('b2b_vn');

  return (
    <Document>
      <Page size={PAGE_SIZE} orientation="landscape" style={styles.page} wrap>
        <Header isVn={isVn} />

        <View style={styles.container}>
          <View style={styles.titleRow}>
            <Text style={styles.title}>Invoice Collection List</Text>

            {/* Date + Salesman, stacked and right-aligned */}
            <View style={styles.infoBlock}>
              <Text style={styles.infoLine}>
                <Text style={styles.infoLabel}>Date : </Text>
                {data?.date_from && data?.date_to
                  ? `${moment(data.date_from).format('DD-MMM-YY')} - ${moment(
                      data.date_to,
                    ).format('DD-MMM-YY')}`
                  : '-'}
              </Text>
              <Text style={styles.infoLine}>
                <Text style={styles.infoLabel}>Salesman : </Text>
                {pdfSafeValue(salesman.name)}
              </Text>
            </View>
          </View>

          {/* Invoice table */}
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]} fixed>
              {COLUMNS.map((col) => (
                <Text
                  key={col.key}
                  style={[
                    styles.cell,
                    styles.headerCell,
                    { width: col.width, textAlign: 'center' },
                  ]}>
                  {col.label}
                </Text>
              ))}
            </View>

            {invoices.map((inv, i) => (
              <View
                key={i}
                style={[
                  styles.tableRow,
                  i % 2 === 1 ? { backgroundColor: STRIPE } : {},
                ]}>
                <Text style={[styles.cell, { width: '3%' }]}>{i + 1}</Text>
                <Text style={[styles.cell, { width: '13%' }]}>
                  {pdfSafeValue(inv.invoice_number)}
                </Text>
                <Text style={[styles.cell, { width: '6%' }]}>
                  {inv.invoice_date
                    ? moment(inv.invoice_date).format('DD/MM/YYYY')
                    : '-'}
                </Text>
                <Text style={[styles.cell, { width: '8%' }]}>
                  {inv.due_date
                    ? moment(inv.due_date).format('DD/MM/YYYY')
                    : '-'}
                </Text>
                <Text style={[styles.cell, { width: '7%' }]}>
                  {pdfSafeValue(inv.customer_code)}
                </Text>
                <Text style={[styles.cell, { width: '19%' }]}>
                  {pdfSafeValue(inv.customer_name)}
                </Text>
                <Text
                  style={[styles.cell, { width: '7%', textAlign: 'right' }]}>
                  {inv.invoice_age_days ?? '-'}
                </Text>
                <Text
                  style={[styles.cell, { width: '8%', textAlign: 'right' }]}>
                  {currency(inv.outstanding_amount || 0, true)}
                </Text>
                <Text style={[styles.cell, { width: '6%' }]}></Text>
                <Text
                  style={[styles.cell, { width: '6%', textAlign: 'right' }]}
                />
                <Text style={[styles.cell, { width: '17%' }]}></Text>
              </View>
            ))}

            {/* Total Outstanding — spans No through Invoice Age, amount lands in the Outstanding Amount column */}
            <View style={[styles.tableRow, styles.totalRow]} wrap={false}>
              <Text
                style={[
                  styles.cell,
                  { width: '63%', textAlign: 'right', fontWeight: 700 },
                ]}>
                TOTAL OUTSTANDING
              </Text>
              <Text
                style={[
                  styles.cell,
                  { width: '8%', textAlign: 'right', fontWeight: 700 },
                ]}>
                {currency(data?.total_outstanding || 0, true)}
              </Text>
              <Text style={[styles.cell, { width: '29%' }]}></Text>
            </View>
          </View>

          {/* Signatures */}
          <View style={styles.signatureWrapper} wrap={false}>
            <View style={styles.signatureBlock}>
              <Text style={styles.signatureLabel}>Prepared by,</Text>
              <View style={styles.signatureSpace} />
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}>
                ({pdfSafeValue(preparedBy.name)})
              </Text>
              <Text style={styles.signatureRole}>
                {preparedBy.role ? deSlug(preparedBy.role) : '-'}
              </Text>
            </View>
            <View style={styles.signatureBlock}>
              <Text style={styles.signatureLabel}>Responsible Salesman,</Text>
              <View style={styles.signatureSpace} />
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}>
                ({pdfSafeValue(salesman.name)})
              </Text>
              <Text style={styles.signatureRole}>Salesman</Text>
            </View>
            <View style={styles.signatureBlock}>
              <Text style={styles.signatureLabel}>Received & Verified by,</Text>
              <View style={styles.signatureSpace} />
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}> </Text>
              <Text style={styles.signatureRole}>
                Sales Support / Branch Head
              </Text>
            </View>
          </View>
        </View>

        <Footer printCount={data?.print_count} />
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: 'Roboto',
    paddingTop: 15,
    paddingBottom: 30,
    color: '#000',
  },
  container: {
    paddingHorizontal: 20,
  },

  /* Title row */
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 14,
    borderBottomWidth: 0.5,
    borderBottomColor: '#ddd',
    paddingBottom: 10,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: NAVY,
  },

  /* Date + Salesman block */
  infoBlock: {
    alignItems: 'flex-end',
  },
  infoLine: {
    fontSize: 8,
    color: '#000',
    marginBottom: 2,
    textAlign: 'right',
  },
  infoLabel: {
    color: NAVY,
    fontWeight: 700,
  },

  /* Table */
  table: {
    marginBottom: 0,
    fontSize: 7,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: BORDER,
    paddingVertical: 6,
    alignItems: 'center',
  },
  tableHeader: {
    backgroundColor: NAVY,
    paddingVertical: 8,
  },
  headerCell: {
    color: '#fff',
    fontWeight: 700,
  },
  cell: {
    paddingHorizontal: 4,
  },
  totalRow: {
    backgroundColor: STRIPE,
    borderBottomWidth: 0,
  },

  /* Signatures */
  signatureWrapper: {
    flexDirection: 'row',
    marginTop: 40,
    fontSize: 8,
  },
  signatureBlock: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  signatureLabel: {
    marginBottom: 4,
  },
  signatureSpace: {
    height: 40,
  },
  signatureLine: {
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
  signatureName: {
    marginTop: 6,
    fontWeight: 700,
  },
  signatureRole: {
    marginTop: 2,
    color: '#555',
  },
});

export default PdfInvoiceCollection;
